import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'components';
import './ActionButtons.scss';

/**
 * ActionButtons Component
 *
 * A reusable component that displays multiple action buttons in a row.
 * Each button can have its own icon, onClick handler, and tooltip.
 *
 * @component
 * @example
 * const actions = [
 *   {
 *     id: 'view',
 *     icon: 'View_document',
 *     title: 'View Details',
 *     onClick: (data) => console.log('View', data),
 *     variant: 'primary'
 *   },
 *   {
 *     id: 'delete',
 *     icon: 'Delete_Icon',
 *     title: 'Delete',
 *     onClick: (data) => console.log('Delete', data),
 *     variant: 'danger'
 *   }
 * ];
 *
 * <ActionButtons actions={actions} data={rowData} iconSize="sm" />
 *
 * @param {Object} props - Component props
 * @param {Array} props.actions - Array of action button configurations
 * @param {string} props.actions[].id - Unique identifier for the action
 * @param {string} props.actions[].icon - Icon name from Icon component
 * @param {string} props.actions[].title - Tooltip/aria-label text
 * @param {string} [props.actions[].tooltip] - Custom tooltip text (defaults to title if not provided)
 * @param {Function} props.actions[].onClick - Click handler function
 * @param {string} [props.actions[].variant] - Button variant (primary, danger, success, warning)
 * @param {boolean} [props.actions[].disabled] - Whether button is disabled
 * @param {Object} [props.data] - Data to pass to onClick handlers
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.align] - Alignment (left, center, right) - default: left
 * @param {string} [props.gap] - Gap size (xs, sm, md, lg) - default: xs
 * @param {string} [props.iconSize] - Icon size (xs, sm, md, lg, xl) - default: md
 */
export const ActionButtons = ({
  actions = [],
  data = null,
  className = '',
  align = 'left',
  gap = 'xs',
  iconSize = 'md',
  value = null,
}) => {
  const [hoveredButton, setHoveredButton] = useState(null);

  // Cleanup hover state on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      setHoveredButton(null);
    };
  }, []);

  if (!actions || actions.length === 0) {
    return null;
  }

  const handleClick = (action, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (action.disabled) return;

    if (typeof action.onClick === 'function') {
      action.onClick(data, e);
    }
  };

  const alignmentClass = `action-buttons--align-${align}`;
  const gapClass = `action-buttons--gap-${gap}`;

  return (
    <div
      className={`action-buttons ${alignmentClass} ${gapClass} ${className}`}
    >
      {actions.map((action) => {
        const {
          id,
          icon,
          title,
          tooltip,
          variant = 'default',
          disabled = false,
        } = action;

        const buttonClass = `action-buttons__button action-buttons__button--${variant}`;
        const tooltipText = tooltip || title;
        const showTooltip = hoveredButton === id && tooltipText;

        return (
          <div key={id} className='action-buttons__wrapper'>
            <button
              className={buttonClass}
              onClick={(e) => handleClick(action, e)}
              onMouseEnter={() => setHoveredButton(id)}
              onMouseLeave={() => setHoveredButton(null)}
              aria-label={title}
              type='button'
              disabled={disabled}
            >
              <Icon name={icon} size={iconSize} />
            </button>
            <div className='action-buttons__value'>{action.value}</div>
            {showTooltip && (
              <div className='action-buttons__tooltip'>{tooltipText}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

ActionButtons.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.oneOf([
        'default',
        'primary',
        'danger',
        'success',
        'warning',
      ]),
      disabled: PropTypes.bool,
    })
  ).isRequired,
  data: PropTypes.any,
  className: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  gap: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  iconSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
};
